
import {
  defineComponent, reactive, watch,
} from 'vue';
import Card from '@/core/components/Tela/Card.vue';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import { IPais } from '@/models/Entidades/Cadastros/Localizacoes/IPais';
import Icone from '@/core/components/Icone.vue';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { useModalBase } from '@/core/composables/ModalBase';
import { useTelaBase } from '@/core/composables/TelaBase';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { ETipoAmbiente } from '@/models/Enumeradores/PainelAdministrativo/ETipoAmbiente';
import storeSistema from '@/store/storeSistema';
import ServicoPais from '@/servicos/Cadastros/Localizacoes/ServicoPais';
import ServicoPAS from '@/servicos/ServicoPAS';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { EStatusCuradoriaCadastro } from '@/models/Enumeradores/PainelAdministrativo/EStatusCuradoriaCadastro';

export default defineComponent({
  name: 'PaisModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
    paisCuradoria: {
      type: Object as () => IPais,
    },
  },
  components: {
    RequisicaoModal,
    Icone,
    Card,
  },
  emits: ['sincronizarRegistro', 'acaoCuradoria', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes,
      preencherPermissoesDados, filtrarPermissaoDadosUsuario, apresentarMensagemSucesso,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);
    const servicoPais = new ServicoPais();
    const servicoPAS = new ServicoPAS();

    telaBase.identificadorRecurso = 'CADASTRO_PAISES';
    telaBase.identificadorPermissao = 'PER_CADASTRO_PAISES';

    const state = reactive({
      pais: {} as IPais,
      aplicarRegrasEnvioCuradoria: false,
      aplicarRegrasRevisaoCuradoria: false,
    });

    function objetoInicial() {
      state.pais = { ativo: true } as IPais;

      state.aplicarRegrasEnvioCuradoria = false;
      state.aplicarRegrasRevisaoCuradoria = false;
    }

    function aprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Aprovado, state.pais);
      modalBase.computedVisivel = false;
    }

    function reprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Reprovado, state.pais);
      modalBase.computedVisivel = false;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();

      if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          retorno = await servicoPais.incluir(state.pais);
        } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          retorno = await servicoPais.alterar(state.pais);
        }

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
            sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
          } else {
            sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
          }
          apresentarMensagemSucesso(retorno.mensagem);
          if (salvarNovo) {
            objetoInicial();
            defineNovaOperacao(props.operacao);
          } else {
            modalBase.computedVisivel = false;
          }
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
        retorno = await servicoPAS.solicitarCadastroCuradoria(telaBase.identificadorRecurso, state.pais);

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          apresentarMensagemSucesso(retorno.mensagem);
          modalBase.computedVisivel = false;
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (UtilitarioGeral.objetoValido(props.paisCuradoria)) {
          state.aplicarRegrasRevisaoCuradoria = true;
          state.pais = UtilitarioGeral.instanciaObjetoLocal(props.paisCuradoria);
        } else {
          if (props.operacao.listaPermissoesDados.length > 0) {
            await preencherPermissoesDados(props.operacao.listaPermissoesDados);
          } else {
            await obterPermissoes(ETipoPermissao.Dados);
          }
          telaBase.permissaoDados = filtrarPermissaoDadosUsuario(storeSistema.getters.codigoEmpresaOperacao());

          if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
            state.aplicarRegrasEnvioCuradoria = false;
            defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
          } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
            modalBase.textoBotaoSalvar = 'Sugerir Cadastro';
            state.aplicarRegrasEnvioCuradoria = true;
          }

          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
            state.pais = await servicoPais.obter(props.operacao.codigoRegistro);
          }
        }
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      modalBase,
      state,
      EPermissaoDados,
      props,
      salvar,
      objetoInicial,
      aprovarCadastro,
      reprovarCadastro,
    };
  },
});
